import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  InputAdornment,
  Typography,
} from '@mui/material';
import { CreditCard as CreditCardIcon } from '@mui/icons-material';
import { CreditCard } from '../db/database';

interface CreditCardFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (card: Omit<CreditCard, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  card?: CreditCard | null;
}

const CreditCardForm: React.FC<CreditCardFormProps> = ({ open, onClose, onSave, card }) => {
  const [name, setName] = useState('');
  const [balance, setBalance] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [minimumPayment, setMinimumPayment] = useState('');
  const [errors, setErrors] = useState<{ name?: string; balance?: string; interestRate?: string }>({});

  useEffect(() => {
    if (card) {
      setName(card.name);
      setBalance(card.balance.toString());
      setInterestRate(card.interestRate.toString());
      setMinimumPayment(card.minimumPayment?.toString() || '');
    } else {
      setName('');
      setBalance('');
      setInterestRate('');
      setMinimumPayment('');
    }
  }, [card, open]);

  const validate = () => {
    const newErrors: { name?: string; balance?: string; interestRate?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Card name is required';
    }

    const balanceNum = parseFloat(balance);
    if (!balance || isNaN(balanceNum) || balanceNum < 0) {
      newErrors.balance = 'Please enter a valid balance';
    }

    const rateNum = parseFloat(interestRate);
    if (!interestRate || isNaN(rateNum) || rateNum < 0 || rateNum > 100) {
      newErrors.interestRate = 'Please enter a valid interest rate (0-100)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      await onSave({
        name: name.trim(),
        balance: parseFloat(balance),
        interestRate: parseFloat(interestRate),
        minimumPayment: minimumPayment ? parseFloat(minimumPayment) : undefined,
      });

      // Reset form
      setName('');
      setBalance('');
      setInterestRate('');
      setMinimumPayment('');
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error saving credit card:', error);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{
            bgcolor: 'error.light',
            borderRadius: '50%',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <CreditCardIcon sx={{ color: 'white' }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {card ? 'Edit Credit Card' : 'Add Credit Card'}
          </Typography>
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2.5}>
            {/* Card Name */}
            <TextField
              label="Card Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              required
              placeholder="e.g., Visa Platinum, Mastercard"
            />

            {/* Balance */}
            <TextField
              label="Current Balance"
              type="number"
              value={balance}
              onChange={(e) => setBalance(e.target.value)}
              error={!!errors.balance}
              helperText={errors.balance}
              fullWidth
              required
              inputProps={{
                step: '0.01',
                min: '0',
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">£</InputAdornment>,
              }}
            />

            {/* Interest Rate */}
            <TextField
              label="Annual Interest Rate (APR)"
              type="number"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              error={!!errors.interestRate}
              helperText={errors.interestRate || 'Enter as percentage (e.g., 18.9 for 18.9%)'}
              fullWidth
              required
              inputProps={{
                step: '0.01',
                min: '0',
                max: '100',
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />

            {/* Minimum Payment */}
            <TextField
              label="Minimum Monthly Payment (Optional)"
              type="number"
              value={minimumPayment}
              onChange={(e) => setMinimumPayment(e.target.value)}
              fullWidth
              inputProps={{
                step: '0.01',
                min: '0',
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">£</InputAdornment>,
              }}
              helperText="Leave blank if you don't know"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary">
            {card ? 'Save Changes' : 'Add Card'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreditCardForm;
